'use client'

import {useI18n} from 'i18n/I18nContext'
import {LocalLink} from 'i18n/LocalLink'
import {organizationApiClient} from 'organization/organizationApiClient'
import {useOrganizationEventRoutes} from 'organization/OrganizationRoutes'
import React, {useEffect, useMemo, useState} from 'react'
import Chart from 'react-apexcharts'
import type {ApexOptions} from 'apexcharts'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PeopleIcon from '@mui/icons-material/People'
import EuroIcon from '@mui/icons-material/Euro'
import PieChartIcon from '@mui/icons-material/PieChart'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import {useRouteOrganizationEventId} from 'utils/lib/url'

export type EventStatisticsPayload = {
  event: {
    id: number
    name: string
    is_public: boolean
    is_ended: boolean
    published_at: string | null
    starts_at: string | null
  }
  capacity: {
    total_seats: number
    occupied_seats: number
    available_seats: number
    fill_rate_percent: number
  }
  attendees: {
    total: number
    paid: number
    complimentary: number
  }
  revenue: {
    gross_sales: number
    platform_fees: number
    net_after_platform_fees: number
    refunded_count: number
    refunded_amount: number
  }
  affiliate_commissions_total: number
  ticket_tiers: {
    event_ticket_id: number | null
    name: string
    sold_count: number
    face_value_total: number
  }[]
  engagement: {
    comments_count: number
  }
  checkout: {
    pending_reservations: number
  }
  sales_breakdown: {
    ticket_sale_lines: number
    product_sale_lines: number
  }
  registrations_by_day: {date: string; count: number}[]
}

export default function EventStatisticsDashboard() {
  const {t} = useI18n()
  const eventId = useRouteOrganizationEventId()
  const routes = useOrganizationEventRoutes()

  const [data, setData] = useState<EventStatisticsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    organizationApiClient()
      .get(`api/event/${eventId}/statistics`)
      .then((res) => {
        if (!cancelled) {
          setData(res as EventStatisticsPayload)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('eventStats.error'))
          setData(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [eventId, t])

  const soldSeatPercent = useMemo(() => {
    if (!data?.capacity.total_seats) return 0
    return Math.min(
      100,
      Math.round(
        (data.capacity.occupied_seats / data.capacity.total_seats) * 1000,
      ) / 10,
    )
  }, [data])

  const radialOptions: ApexOptions = useMemo(
    () => ({
      series: [soldSeatPercent],
      chart: {
        height: 200,
        type: 'radialBar',
        toolbar: {show: true},
      },
      plotOptions: {
        radialBar: {
          hollow: {
            size: '75%',
            background: '#2C2C2C',
            margin: 10,
          },
          track: {
            background: 'rgba(255, 163, 0, 1)',
            strokeWidth: '10%',
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: -7,
              show: true,
              color: '#7C7C7C',
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 'regular',
            },
            value: {
              offsetY: 7,
              color: '#fff',
              fontSize: '18px',
              show: true,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 'bold',
            },
          },
        },
      },
      labels: [t('eventStats.soldShareLabel')],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          colorStops: [
            {offset: 20, color: '#FA631E', opacity: 1},
            {offset: 60, color: '#FFAD01', opacity: 1},
          ],
        },
      },
    }),
    [soldSeatPercent, t],
  )

  const registrationChartOptions: ApexOptions = useMemo(() => {
    const labels = data?.registrations_by_day.map((d) => d.date.slice(5)) ?? []
    return {
      chart: {
        type: 'area',
        height: 280,
        toolbar: {show: false},
        zoom: {enabled: false},
      },
      dataLabels: {enabled: false},
      stroke: {curve: 'smooth', width: 2},
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
        },
      },
      colors: ['#FA631E'],
      xaxis: {
        categories: labels,
        labels: {rotate: -45, style: {colors: '#7C7C7C'}},
      },
      yaxis: {
        labels: {style: {colors: '#7C7C7C'}},
        min: 0,
        forceNiceScale: true,
      },
      grid: {borderColor: '#333'},
    }
  }, [data])

  const registrationSeries = useMemo(() => {
    return [
      {
        name: t('eventStats.registrationsSeries'),
        data: data?.registrations_by_day.map((d) => d.count) ?? [],
      },
    ]
  }, [data, t])

  if (loading) {
    return (
      <div className="col-12">
        <div className="card__wrapper p-30">
          <p className="mb-0">{t('eventStats.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="col-12">
        <div className="card__wrapper p-30">
          <p className="mb-0 text-danger">{error ?? t('eventStats.error')}</p>
        </div>
      </div>
    )
  }

  const ev = data.event
  const statusLabel = ev.is_ended
    ? t('eventStats.statusEnded')
    : ev.is_public
      ? t('eventStats.statusPublished')
      : t('eventStats.statusDraft')

  return (
    <>
      <div className="col-12 px-10 pb-25">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h3 className="mb-5">{t('eventStats.title')}</h3>
            <p className="mb-0 text-muted" style={{fontSize: '0.95rem'}}>
              {ev.name} · {statusLabel}
              {ev.starts_at
                ? ` · ${t('eventStats.starts')}: ${ev.starts_at}`
                : ''}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <LocalLink
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
              to={routes.earning}
            >
              {t('eventStats.linkEarnings')}
              <OpenInNewIcon style={{fontSize: 18}} />
            </LocalLink>
            <LocalLink
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
              to={routes.attendants}
            >
              {t('eventStats.linkAttendees')}
              <OpenInNewIcon style={{fontSize: 18}} />
            </LocalLink>
          </div>
        </div>
      </div>

      <div className="col-xxl-3 col-xl-6 col-lg-6">
        <div className="card__wrapper h-100 p-30">
          <div className="d-flex align-items-center gap-2 mb-10">
            <PeopleIcon style={{color: '#FA631E'}} />
            <span className="text-muted">
              {t('eventStats.kpiRegistrations')}
            </span>
          </div>
          <h2 className="mb-0">{data.attendees.total}</h2>
          <p className="mb-0 mt-10 small text-muted">
            {t('eventStats.kpiPaidLabel')} {data.attendees.paid} ·{' '}
            {t('eventStats.kpiComplimentaryLabel')}{' '}
            {data.attendees.complimentary}
          </p>
        </div>
      </div>

      <div className="col-xxl-3 col-xl-6 col-lg-6">
        <div className="card__wrapper h-100 p-30">
          <div className="d-flex align-items-center gap-2 mb-10">
            <EuroIcon style={{color: '#FA631E'}} />
            <span className="text-muted">{t('eventStats.kpiNetRevenue')}</span>
          </div>
          <h2 className="mb-0">
            {data.revenue.net_after_platform_fees.toFixed(2)} €
          </h2>
          <p className="mb-0 mt-10 small text-muted">
            {t('eventStats.kpiGrossHint')} {data.revenue.gross_sales.toFixed(2)}{' '}
            €
          </p>
        </div>
      </div>

      <div className="col-xxl-3 col-xl-6 col-lg-6">
        <div className="card__wrapper h-100 p-30">
          <div className="d-flex align-items-center gap-2 mb-10">
            <PieChartIcon style={{color: '#FA631E'}} />
            <span className="text-muted">{t('eventStats.kpiCapacity')}</span>
          </div>
          <h2 className="mb-0">{data.capacity.fill_rate_percent}%</h2>
          <p className="mb-0 mt-10 small text-muted">
            {data.capacity.occupied_seats} / {data.capacity.total_seats}{' '}
            {t('eventStats.kpiSeatsHint')}
          </p>
        </div>
      </div>

      <div className="col-xxl-3 col-xl-6 col-lg-6">
        <div className="card__wrapper h-100 p-30">
          <div className="d-flex align-items-center gap-2 mb-10">
            <ChatBubbleOutlineIcon style={{color: '#FA631E'}} />
            <span className="text-muted">{t('eventStats.kpiComments')}</span>
          </div>
          <h2 className="mb-0">{data.engagement.comments_count}</h2>
          <p className="mb-0 mt-10 small text-muted">
            {t('eventStats.kpiPendingCheckout')}{' '}
            {data.checkout.pending_reservations}
          </p>
        </div>
      </div>

      <div className="col-xxl-4 col-xl-6">
        <div className="card__wrapper total__seat-wapper h-100 px-30 py-25">
          <div className="card__header mb-35">
            <div className="card__header-top">
              <div className="card__title-inner">
                <div className="card__header-icon">
                  <i className="flaticon-chair-2"></i>
                </div>
                <div className="card__header-title">
                  <h4>{t('eventStats.capacityCardTitle')}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="total__seat-progress">
            <div id="radialChartEventStats">
              <Chart
                options={radialOptions}
                series={radialOptions.series}
                type="radialBar"
                height={200}
              />
            </div>
            <div className="redialchart__content">
              <div className="redialchart__info">
                <span>{t('orgStats.totalSeatsLabel')}</span>
                <h4>{data.capacity.total_seats}</h4>
              </div>
              <div className="redialchart__info">
                <span>{t('orgStats.freeSeatsLabel')}</span>
                <h4>{data.capacity.available_seats}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xxl-8 col-xl-6">
        <div className="card__wrapper h-100 p-30">
          <div className="card__header mb-20">
            <div className="card__header-top">
              <div className="card__title-inner">
                <div className="card__header-icon">
                  <EuroIcon />
                </div>
                <div className="card__header-title">
                  <h4>{t('eventStats.revenueCardTitle')}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="card__inner pt-5">
            <div className="row g-20">
              <div className="col-md-4">
                <span className="text-muted d-block small">
                  {t('eventStats.grossSales')}
                </span>
                <strong className="fs-5">
                  {data.revenue.gross_sales.toFixed(2)} €
                </strong>
              </div>
              <div className="col-md-4">
                <span className="text-muted d-block small">
                  {t('eventStats.platformFees')}
                </span>
                <strong className="fs-5">
                  {data.revenue.platform_fees.toFixed(2)} €
                </strong>
              </div>
              <div className="col-md-4">
                <span className="text-muted d-block small">
                  {t('eventStats.netAfterFees')}
                </span>
                <strong className="fs-5">
                  {data.revenue.net_after_platform_fees.toFixed(2)} €
                </strong>
              </div>
              <div className="col-md-6">
                <span className="text-muted d-block small">
                  {t('eventStats.refunds')}
                </span>
                <span>
                  {data.revenue.refunded_count} ·{' '}
                  {data.revenue.refunded_amount.toFixed(2)} €
                </span>
              </div>
              <div className="col-md-6">
                <span className="text-muted d-block small">
                  {t('eventStats.affiliateCommissions')}
                </span>
                <span>{data.affiliate_commissions_total.toFixed(2)} €</span>
              </div>
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <ShoppingCartIcon fontSize="small" style={{color: '#888'}} />
                  <span className="small text-muted">
                    {t('eventStats.ticketSaleLines')}{' '}
                    {data.sales_breakdown.ticket_sale_lines} ·{' '}
                    {t('eventStats.productSaleLines')}{' '}
                    {data.sales_breakdown.product_sale_lines}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xxl-6">
        <div className="card__wrapper h-100 p-30">
          <div className="card__header mb-15">
            <div className="card__header-top">
              <div className="card__title-inner">
                <div className="card__header-title">
                  <h4>{t('eventStats.tiersTitle')}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="table-responsive pt-5 pb-10">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>{t('eventStats.tierColTier')}</th>
                  <th className="text-end">{t('eventStats.tierColSold')}</th>
                  <th className="text-end">{t('eventStats.tierColFace')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ticket_tiers.length === 0 && (
                  <tr>
                    <td colSpan={3}>{t('eventStats.tiersEmpty')}</td>
                  </tr>
                )}
                {data.ticket_tiers.map((row, idx) => (
                  <tr key={`${row.event_ticket_id ?? 'x'}-${idx}`}>
                    <td>{row.name}</td>
                    <td className="text-end">{row.sold_count}</td>
                    <td className="text-end">
                      {row.face_value_total.toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="col-xxl-6">
        <div className="card__wrapper h-100 p-30">
          <div className="card__header mb-10">
            <div className="card__header-top">
              <div className="card__title-inner">
                <div className="card__header-title">
                  <h4>{t('eventStats.registrationsChartTitle')}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-10 pb-5">
            <Chart
              options={registrationChartOptions}
              series={registrationSeries}
              type="area"
              height={280}
            />
          </div>
        </div>
      </div>
    </>
  )
}
